import { useState, useEffect } from 'preact/hooks'

/**
 * Custom hook to update state based on URL search parameter changes.
 * @param {string} paramName - The search param key to track in the URL (e.g., "search").
 * @param {any} defaultValue - The default value to use if the search param doesn't exist.
 * @returns {Array} - The current value of the search param and a setter function.
 */
export const useSearchQuery = (paramName, defaultValue) => {
  const [paramValue, setParamValue] = useState(() => {
    // Extract the query parameter from the current URL
    const urlParams = new URLSearchParams(window.location.search)
    return urlParams.get(paramName) || defaultValue
  })

  const handleLocationChange = () => {
    // Every time the URL changes, re-fetch the query parameter
    const urlParams = new URLSearchParams(window.location.search)
    const newValue = urlParams.get(paramName) || defaultValue
    setParamValue(newValue)
  }

  useEffect(() => {
    // Initially check when the component mounts
    handleLocationChange()

    // Listen for popstate (back/forward navigation) or hash changes (if applicable)
    window.addEventListener('variant-selected', handleLocationChange)

    // Cleanup listener on component unmount
    return () => {
      window.removeEventListener('variant-selected', handleLocationChange)
    }
  }, [paramName, defaultValue]) // Re-run effect when paramName or defaultValue changes

  return [paramValue]
}
